import React from "react";

import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useQuery } from "@apollo/client";
import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import { GET_MEMBER_SHOPPING_CART, GET_WINE } from "../../graphql/queries";
import { GET_AUTH } from "../../graphql/resolvers/auth";
import { FETCH_POLICY_CACHE_ONLY, I18N_NAMESPACES } from "../../helpers/constants";
import { usePrerenderReady } from "../../helpers/hooks";
import urlPatterns from "../../urls";

import BottleInfo from "../BottleInfo";
import StyledLoading from "../../components/atoms/Loading/StyledLoading";
import PageMeta from "../../components/molecules/PageMeta";
import JsonLdProduct from "../../components/atoms/JsonLdProduct";
import { getCountry } from "../../helpers/urls.helper";

/**
 * Renders Wine details page.
 * React.Component: https://reactjs.org/docs/react-component.html
 * */
const WineDetails = ({ authQuery, match, memberShoppingCartQuery, history }) => {
  const { slug } = match.params;
  const memberId = authQuery.auth && authQuery.auth.memberId;
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);

  const { loading, data } = useQuery(GET_WINE, {
    variables: { slug, memberId },
    partialRefetch: true,
    skip: !slug,
  });

  const isLoading = loading || memberShoppingCartQuery.loading;
  usePrerenderReady(!isLoading);

  if (isLoading) {
    return (
      <>
        <StyledLoading />
      </>
    );
  }

  if (data.wine === null) {
    return <Redirect to={urlPatterns.NOT_FOUND} />;
  }
  const currency = getCountry() === "uk" ? "GBP" : "AUD";
  const productUrl = `${import.meta.env.REACT_APP_BASE_URL}${urlPatterns.WINE_DETAILS(slug)}`;

  return (
    <>
      <PageMeta
        title={t("common:pageMetaTitle", {
          title: data.wine.product.name,
          interpolation: { escapeValue: false },
        })}
        description={data.wine.shortDescription}
        image={data.wine.product.coverPhotoLarge}
      />
      <JsonLdProduct
        name={data.wine.product.name}
        description={data.wine.shortDescription || ""}
        image={data.wine.product.coverPhotoLarge || ""}
        price={data.wine.product.sellingPrice || 0}
        currency={currency}
        url={productUrl}
        hasStock={data.wine.hasStock ?? false}
        brand={data.wine.wineMaker?.name || "Good Pair Days"}
      />
      <BottleInfo
        wine={data.wine}
        memberId={memberId}
        memberShoppingCartQuery={memberShoppingCartQuery}
        history={history}
      />
    </>
  );
};

WineDetails.propTypes = {
  match: PropTypes.shape({}).isRequired,
  authQuery: PropTypes.shape({}).isRequired,
  memberShoppingCartQuery: PropTypes.shape({}).isRequired,
  history: PropTypes.shape({}).isRequired,
};

export default compose(
  graphql(GET_AUTH, {
    name: "authQuery",
    options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
  }),
  graphql(GET_MEMBER_SHOPPING_CART, {
    name: "memberShoppingCartQuery",
    options: { partialRefetch: true },
  })
)(WineDetails);
